// src/components/BlogContainer.js
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import Blog from './Blog';
import BlogEditor from './BlogEditor';
import BlogAdmin from './BlogAdmin';

export default function BlogContainer({ user }) {
  const [view, setView] = useState('blog'); // 'blog', 'editor', 'admin'
  const [isAuthor, setIsAuthor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user is a blog author
  const checkAuthorStatus = useCallback(async () => {
    if (!user?.id) {
      setIsAuthor(false);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('blog_authors')
        .select('is_admin')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking author status:', error);
      }

      const authorStatus = !!data;
      const adminStatus = data?.is_admin || false;

      setIsAuthor(authorStatus);
      setIsAdmin(adminStatus);
    } catch (err) {
      console.error('Error checking author status:', err);
      setIsAuthor(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    checkAuthorStatus();
  }, [checkAuthorStatus]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading blog...</p>
        </div>
      </div>
    );
  }

  // Render appropriate view
  switch (view) {
    case 'editor':
      return (
        <BlogEditor
          user={user}
          onBack={() => setView('blog')}
        />
      );
    
    case 'admin':
      return (
        <BlogAdmin
          user={user}
          onBack={() => setView('blog')}
          isAdmin={isAdmin}
        />
      );
    
    default:
      return (
        <div className="space-y-6">
          {/* Admin button for admins */}
          {isAdmin && (
            <div className="flex justify-end">
              <button
                onClick={() => setView('admin')}
                className="bg-blue-500/80 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm flex items-center gap-2"
              >
               Manage Authors
              </button>
            </div>
          )}

          <Blog
            user={user}
            onNavigateToEditor={() => setView('editor')}
            isAuthor={isAuthor}
          />
        </div>
      );
  }
}
